import React, { useEffect, useState } from 'react';
import './DebouncedInput.scss';

/**
 * @description DebouncedInput component that delays the onChange callback until the user stops typing.
 * This helps reduce unnecessary processing when the input value changes rapidly, particularly useful for search and filter inputs.
 *
 * @SADtrace SAD-003-2.1, SAD-003-3.1, SAD-003-4.1, SAD-003-5.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {Object} props - Component props
 * @param {string|number} props.value - The initial/controlled value of the input
 * @param {Function} props.onChange - Callback function triggered after the debounce period
 * @param {number} [props.debounce=500] - Debounce delay in milliseconds
 *
 * @returns {JSX.Element} A debounced input element
 */
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
};

export default DebouncedInput;
